import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import AuthFooter from 'components/auth/AuthFooter';
import AuthInnerContainer from 'components/auth/AuthInnerContainer';
import AuthOuterContainer from 'components/auth/AuthOuterContainer';

import Form from 'components/shared/Form';
import Logo from 'components/shared/Logo';
import ThemeColor from 'components/shared/ThemeColor';

import { ToastContext } from 'components/shared/context/ToastContextProvider';
import { UserContext } from 'components/shared/context/UserContextProvider';

import ValidatedBaseInput from 'components/shared/inputs/ValidatedBaseInput';

import sansanTech from 'services/sansantech';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();

  const { userDispatch } = useContext(UserContext);
  const { toastDispatch } = useContext(ToastContext);

  const [submitting, setSubmitting] = useState<boolean>(false);

  const [emailValue, setEmailValue] = useState<string>('');
  const [isEmailValueValid, setIsEmailValueValid] = useState<boolean>(false);

  const [passwordValue, setPasswordValue] = useState<string>('');
  const [isPasswordValueValid, setIsPasswordValueValid] =
    useState<boolean>(false);

  const [confirmPasswordValue, setConfirmPasswordValue] = useState<string>('');
  const [isConfirmPasswordValueValid, setIsConfirmPasswordValueValid] =
    useState<boolean>(false);

  const onSubmit = useCallback(() => {
    setSubmitting(true);

    sansanTech
      .signUp({ email: emailValue, password: passwordValue })
      .then((response) => {
        console.log(response);
        if (userDispatch) {
          userDispatch({
            type: 'SIGNIN',
            payload: {
              isOwner: response.data.isOwner,
              token: response.data.token,
              company: response.data.company,
            },
          });
          setSubmitting(false);
          toastDispatch?.({ type: 'SUCCESS', message: 'Signed up' });
          navigate('/', { replace: true });
        } else {
          setSubmitting(false);
          toastDispatch?.({ type: 'ERROR', message: 'Something went wrong' });
        }
      })
      .catch((error) => {
        console.log(error);
        setSubmitting(false);
        toastDispatch?.({
          type: 'ERROR',
          message: 'Email is taken',
        });
      });
  }, [
    setSubmitting,
    emailValue,
    passwordValue,
    userDispatch,
    navigate,
    toastDispatch,
  ]);

  const disabled = useMemo(
    () =>
      !(
        isEmailValueValid &&
        isPasswordValueValid &&
        isConfirmPasswordValueValid
      ),
    [isEmailValueValid, isPasswordValueValid, isConfirmPasswordValueValid]
  );

  return (
    <ThemeColor color="gray">
      <AuthOuterContainer>
        <AuthInnerContainer>
          <Logo scale={60} showText={true} />

          <Form
            header="Sign up"
            onSubmit={onSubmit}
            disabled={disabled}
            submitting={submitting}
            submitButtonText="Sign up"
            submitButtonWidth="100px"
          >
            <ValidatedBaseInput
              label="email"
              type="text"
              value={emailValue}
              setValue={setEmailValue}
              setIsValid={setIsEmailValueValid}
              validators={z
                .string()
                .min(1, { message: 'This field is required' })
                .email({ message: 'Must be a valid email' })}
            />
            <ValidatedBaseInput
              label="password"
              type="password"
              value={passwordValue}
              setValue={setPasswordValue}
              setIsValid={setIsPasswordValueValid}
              validators={z
                .string()
                .min(1, { message: 'This field is required' })
                .min(8, { message: 'Must be at least 8 characters' })}
            />
            <ValidatedBaseInput
              label="confirm password"
              type="password"
              value={confirmPasswordValue}
              setValue={setConfirmPasswordValue}
              setIsValid={setIsConfirmPasswordValueValid}
              validators={z
                .string()
                .min(1, { message: 'This field is required' })
                .regex(new RegExp(`^${passwordValue}$`), {
                  message: 'Passwords must match',
                })}
              watch={passwordValue}
            />
          </Form>

          <AuthFooter>
            Already have an account?&nbsp;
            <Link to="/sign-in">Sign in here</Link>
          </AuthFooter>
        </AuthInnerContainer>
      </AuthOuterContainer>
    </ThemeColor>
  );
};

export default SignUpPage;
